import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  TablePagination,
  PageLoader,
} from '../../../../components/ui';
import { ActiveBadge } from '../MasterDataPage';
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_DOC_TYPES,
  EQUIPMENT_FIELD_KEYS,
  VEHICLE_CLASSES,
  defaultCustomDocs,
  defaultDocRequirements,
  defaultFieldRequirements,
  isPlaceholderCustomDocKey,
  makeTempCustomDocKey,
} from '../../../../data/master/equipments';
import {
  createEquipmentType,
  listEquipmentTypes,
  updateEquipmentType,
} from '../../../../repositories/equipmentType.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useHubHeaderActions } from '../../hub/hubUi';
import { useTableSort } from '../../../../hooks/useTableSort';

const emptyForm = () => ({
  name: '',
  category: 'Agriculture',
  categoryOther: '',
  vehicleClass: 'LMV',
  description: '',
  active: true,
  fieldRequirements: defaultFieldRequirements(),
  docRequirements: defaultDocRequirements(),
  customDocs: defaultCustomDocs(),
});

const LEVEL_OPTIONS = [
  { value: 'required', short: 'Req', title: 'Required' },
  { value: 'optional', short: 'Opt', title: 'Optional' },
  { value: 'hidden', short: 'Hide', title: 'Hidden' },
];

const CUSTOM_LEVEL_OPTIONS = [
  { value: 'required', short: 'Req', title: 'Required' },
  { value: 'optional', short: 'Opt', title: 'Optional' },
];

function RequirementToggle({ value, onChange, options = LEVEL_OPTIONS }) {
  return (
    <HStack spacing={1} flexShrink={0}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <Box
            key={opt.value}
            as="button"
            type="button"
            title={opt.title}
            onClick={() => onChange(opt.value)}
            px={2}
            py={1}
            minW="40px"
            textAlign="center"
            borderRadius="sm"
            borderWidth="1px"
            borderColor={selected ? 'utkarsh.amber' : 'border.subtle'}
            bg={selected ? 'utkarsh.amber' : 'bg.surface'}
            fontSize="11px"
            fontWeight={selected ? '700' : '500'}
            color={selected ? 'text.onAmber' : 'text.primary'}
            lineHeight="1.2"
            _hover={{ borderColor: selected ? 'utkarsh.amber' : 'text.secondary' }}
          >
            {opt.short}
          </Box>
        );
      })}
    </HStack>
  );
}

function RequirementRow({ label, value, onChange, options }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={2}
      px={2.5}
      py={1.5}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      minH="40px"
    >
      <Text fontSize="13px" fontWeight="500" noOfLines={1} title={label} flex="1" minW={0}>
        {label}
      </Text>
      <RequirementToggle value={value} onChange={onChange} options={options} />
    </Flex>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <Box
      p={3}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text fontSize="11px" color="text.secondary" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="14px" fontWeight="600" fontFamily={mono ? 'mono' : 'inherit'}>
        {value || '—'}
      </Text>
    </Box>
  );
}

function levelLabel(level) {
  if (level === 'required') return 'Required';
  if (level === 'hidden') return 'Hidden';
  return 'Optional';
}

function levelColor(level) {
  if (level === 'required') return 'green';
  if (level === 'hidden') return 'gray';
  return 'orange';
}

function RequirementChip({ label, level }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={2}
      px={2.5}
      py={1.5}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      minH="36px"
    >
      <Text fontSize="13px" fontWeight="500" noOfLines={1} title={label} flex="1" minW={0}>
        {label}
      </Text>
      <Text
        fontSize="11px"
        fontWeight="700"
        color={`${levelColor(level)}.500`}
        flexShrink={0}
      >
        {levelLabel(level)}
      </Text>
    </Flex>
  );
}

function formatStamp(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

export default function EquipmentsPage({ embedded = false } = {}) {
  const toast = useToast();
  const addModal = useDisclosure();
  const detailModal = useDisclosure();
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [modalTab, setModalTab] = useState(0);
  const [selected, setSelected] = useState(null);

  const loadEquipments = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      try {
        const result = await listEquipmentTypes({ pageSize: 500 });
        setEquipments(result.items || []);
      } catch (err) {
        toast({
          title: err?.message || 'Failed to load equipment types',
          status: 'error',
          position: 'top-right',
        });
        setEquipments([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const knownCategories = EQUIPMENT_CATEGORIES.filter((c) => c !== 'Other');
    return equipments.filter((e) => {
      const matchQ =
        !q ||
        (e.name || '').toLowerCase().includes(q) ||
        (e.equipmentId || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q);
      const matchCat =
        !categoryFilter ||
        (categoryFilter === 'Other'
          ? !knownCategories.includes(e.category)
          : e.category === categoryFilter);
      const matchClass = !classFilter || e.vehicleClass === classFilter;
      return matchQ && matchCat && matchClass;
    });
  }, [equipments, search, categoryFilter, classFilter]);

  const { sorted, sort, toggleSort } = useTableSort(filtered);

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingId(null);
    setEditingCode(null);
    setForm(emptyForm());
    setModalTab(0);
    addModal.onOpen();
  };

  useHubHeaderActions(
    () =>
      embedded ? (
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
          Add equipment type
        </PrimaryButton>
      ) : null,
    [embedded],
  );

  const openEdit = (row) => {
    const known = EQUIPMENT_CATEGORIES.includes(row.category);
    setEditingId(row.id);
    setEditingCode(row.equipmentId || null);
    setForm({
      name: row.name || '',
      category: known ? row.category : 'Other',
      categoryOther: known ? '' : row.category || '',
      vehicleClass: row.vehicleClass || 'LMV',
      description: row.description || '',
      active: row.active !== false,
      fieldRequirements: {
        ...defaultFieldRequirements(),
        ...(row.fieldRequirements || {}),
      },
      docRequirements: {
        ...defaultDocRequirements(),
        ...(row.docRequirements || {}),
      },
      customDocs: Array.isArray(row.customDocs)
        ? row.customDocs.map((d) => ({
            key: d.key || '',
            label: d.label || '',
            level: d.level === 'required' ? 'required' : 'optional',
          }))
        : [],
    });
    setModalTab(0);
    addModal.onOpen();
  };

  const openDetails = (row) => {
    setSelected(row);
    detailModal.onOpen();
  };

  const openEditFromDetails = () => {
    if (!selected) return;
    detailModal.onClose();
    openEdit(selected);
  };

  const addCustomDoc = () => {
    setForm((f) => ({
      ...f,
      customDocs: [
        ...f.customDocs,
        {
          key: '', // generated from label on save
          label: '',
          level: 'required',
        },
      ],
    }));
  };

  const updateCustomDoc = (index, patch) => {
    setForm((f) => {
      const next = f.customDocs.map((d, i) => {
        if (i !== index) return d;
        const merged = { ...d, ...patch };
        // Keep key in sync with label while editing (until saved with a stable key).
        if (
          patch.label !== undefined &&
          (!merged.key || isPlaceholderCustomDocKey(merged.key))
        ) {
          const used = f.customDocs
            .map((x, j) => (j === index ? null : x.key))
            .filter(Boolean);
          merged.key = makeTempCustomDocKey(merged.label || 'doc', used);
        }
        return merged;
      });
      return { ...f, customDocs: next };
    });
  };

  const removeCustomDoc = (index) => {
    setForm((f) => ({
      ...f,
      customDocs: f.customDocs.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({
        title: 'Equipment type name is required',
        status: 'warning',
        position: 'top-right',
      });
      setModalTab(0);
      return;
    }
    const category =
      form.category === 'Other' ? form.categoryOther.trim() : form.category;
    if (!category) {
      toast({
        title: form.category === 'Other' ? 'Enter a custom category' : 'Category is required',
        status: 'warning',
        position: 'top-right',
      });
      setModalTab(0);
      return;
    }

    const blankCustom = form.customDocs.find((d) => !(d.label || '').trim());
    if (blankCustom) {
      toast({
        title: 'Enter a name for each custom document (or remove empty rows)',
        status: 'warning',
        position: 'top-right',
      });
      setModalTab(2);
      return;
    }

    const payload = {
      name: form.name.trim(),
      category,
      vehicleClass: form.vehicleClass,
      description: form.description.trim(),
      active: form.active,
      fieldRequirements: form.fieldRequirements,
      docRequirements: form.docRequirements,
      customDocs: form.customDocs.map((d) => ({
        key: d.key,
        label: d.label.trim(),
        level: d.level === 'required' ? 'required' : 'optional',
      })),
    };

    setSaving(true);
    try {
      if (editingId != null) {
        await updateEquipmentType(editingId, payload);
        toast({
          title: 'Equipment type updated',
          description: `${payload.name}${editingCode ? ` · ${editingCode}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createEquipmentType(payload);
        toast({
          title: 'Equipment type saved',
          description: `${payload.name}${created?.equipmentId ? ` · ${created.equipmentId}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      }
      addModal.onClose();
      await loadEquipments({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save equipment type',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading equipment types…" />;
  }

  return (
    <Box>
      {!embedded ? (
        <Flex
          justify="space-between"
          align="center"
          gap={4}
          mb={5}
          wrap="wrap"
        >
          <Heading size="lg">Equipments Master</Heading>
          <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
            Add equipment type
          </PrimaryButton>
        </Flex>
      ) : null}

      <AppCard mb={4} py={3} px={4}>
        <Flex
          align="flex-end"
          gap={3}
          wrap="wrap"
        >
          <Box flex="1" minW={{ base: '100%', sm: '200px' }} maxW={{ md: '260px' }}>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Name / ID / description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box w={{ base: '100%', sm: '180px' }}>
            <AppSelect
              label="Category"
              labelFontSize="13px"
              h="40px"
              placeholder="All categories"
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v || '')}
              options={[
                { value: '', label: 'All categories' },
                ...EQUIPMENT_CATEGORIES.map((c) => ({ value: c, label: c })),
              ]}
            />
          </Box>
          <Box w={{ base: '100%', sm: '180px' }}>
            <AppSelect
              label="Vehicle class"
              labelFontSize="13px"
              h="40px"
              placeholder="All classes"
              value={classFilter}
              onChange={(v) => setClassFilter(v || '')}
              options={[
                { value: '', label: 'All classes' },
                ...VEHICLE_CLASSES.map((c) => ({ value: c, label: c })),
              ]}
            />
          </Box>
          <OutlineButton
            size="sm"
            h="40px"
            minH="40px"
            flexShrink={0}
            onClick={() => {
              setSearch('');
              setCategoryFilter('');
              setClassFilter('');
            }}
          >
            Clear
          </OutlineButton>
        </Flex>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Text fontSize="13px" color="text.secondary">
            {totalCount} equipment type{totalCount === 1 ? '' : 's'}
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="equipmentId" sort={sort} onSort={toggleSort}>
                  ID
                </SortableTh>
                <SortableTh columnKey="name" sort={sort} onSort={toggleSort}>
                  Equipment type
                </SortableTh>
                <SortableTh columnKey="category" sort={sort} onSort={toggleSort}>
                  Category
                </SortableTh>
                <SortableTh columnKey="vehicleClass" sort={sort} onSort={toggleSort}>
                  Vehicle class
                </SortableTh>
                <SortableTh columnKey="active" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map((row) => (
                <Tr
                  key={row.id ?? row.equipmentId}
                  cursor="pointer"
                  _hover={{ bg: 'bg.hover' }}
                  onClick={() => openDetails(row)}
                >
                  <Td fontFamily="mono" fontSize="13px">
                    {row.equipmentId}
                  </Td>
                  <Td fontWeight="600">{row.name}</Td>
                  <Td>{row.category}</Td>
                  <Td>{row.vehicleClass}</Td>
                  <Td>
                    <ActiveBadge active={row.active} />
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      aria-label={`Edit ${row.name}`}
                      icon={<MdEdit />}
                      size="sm"
                      variant="ghostInk"
                      onClick={() => openEdit(row)}
                    />
                  </Td>
                </Tr>
              ))}
              {pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No equipment types match your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </AppCard>

      <Modal
        isOpen={addModal.isOpen}
        onClose={() => {
          if (!saving) addModal.onClose();
        }}
        size="4xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          as="form"
          onSubmit={handleSave}
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0} pb={2}>
            {editingId != null
              ? `Edit equipment type · ${editingCode || editingId}`
              : 'Add equipment type'}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody flex="1" overflowY="auto" minH={0} pt={0} pb={4}>
            <Tabs
              index={modalTab}
              onChange={setModalTab}
              variant="enclosed"
              colorScheme="yellow"
              isLazy
            >
              <TabList mb={4} flexShrink={0}>
                <Tab fontSize="13px" fontWeight="600">
                  Basics
                </Tab>
                <Tab fontSize="13px" fontWeight="600">
                  Form fields
                </Tab>
                <Tab fontSize="13px" fontWeight="600">
                  Documents
                  {form.customDocs.length > 0 ? ` (${form.customDocs.length})` : ''}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0} py={0}>
                  <VStack align="stretch" spacing={4}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <AppInput
                        label="Equipment name"
                        isRequired
                        placeholder="e.g. Tractor"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      />
                      <AppSelect
                        label="Category"
                        isRequired
                        value={form.category}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            category: v,
                            categoryOther: v === 'Other' ? f.categoryOther : '',
                          }))
                        }
                        options={EQUIPMENT_CATEGORIES.map((c) => ({ value: c, label: c }))}
                      />
                      <AppSelect
                        label="Vehicle class"
                        isRequired
                        value={form.vehicleClass}
                        onChange={(v) => setForm((f) => ({ ...f, vehicleClass: v }))}
                        options={VEHICLE_CLASSES.map((c) => ({ value: c, label: c }))}
                      />
                    </SimpleGrid>

                    {form.category === 'Other' ? (
                      <AppInput
                        label="Custom category"
                        isRequired
                        placeholder="Type category name"
                        value={form.categoryOther}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, categoryOther: e.target.value }))
                        }
                      />
                    ) : null}

                    <AppTextarea
                      label="Description"
                      placeholder="Short note about this equipment type"
                      value={form.description}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, description: e.target.value }))
                      }
                      rows={3}
                    />

                    <HStack
                      justify="space-between"
                      px={3}
                      py={2}
                      bg="bg.input"
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor="border.subtle"
                    >
                      <FormLabel mb={0} fontSize="13px">
                        Active
                      </FormLabel>
                      <Switch
                        isChecked={form.active}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, active: e.target.checked }))
                        }
                        colorScheme="yellow"
                      />
                    </HStack>
                  </VStack>
                </TabPanel>

                <TabPanel px={0} py={0}>
                  <Text fontSize="12px" color="text.secondary" mb={3}>
                    Req = must fill on mobile · Opt = optional · Hide = not shown.
                    Hover chips for full labels.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {EQUIPMENT_FIELD_KEYS.map(({ key, label }) => (
                      <RequirementRow
                        key={key}
                        label={label}
                        value={form.fieldRequirements?.[key] || 'optional'}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            fieldRequirements: {
                              ...f.fieldRequirements,
                              [key]: v,
                            },
                          }))
                        }
                      />
                    ))}
                  </SimpleGrid>
                </TabPanel>

                <TabPanel px={0} py={0}>
                  <Text fontSize="12px" color="text.secondary" mb={3}>
                    Defaults: 4 photos + PUC. Add exception docs below when a type needs
                    something extra (e.g. heavy testing for crane).
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={5}>
                    {EQUIPMENT_DOC_TYPES.map(({ key, label }) => (
                      <RequirementRow
                        key={key}
                        label={label}
                        value={form.docRequirements?.[key] || 'optional'}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            docRequirements: {
                              ...f.docRequirements,
                              [key]: v,
                            },
                          }))
                        }
                      />
                    ))}
                  </SimpleGrid>

                  <Flex align="center" justify="space-between" gap={3} mb={3} wrap="wrap">
                    <Box>
                      <Text fontSize="13px" fontWeight="700">
                        Custom / exception documents
                      </Text>
                      <Text fontSize="12px" color="text.secondary">
                        Name the document and mark required or optional.
                      </Text>
                    </Box>
                    <OutlineButton size="sm" leftIcon={<MdAdd />} onClick={addCustomDoc}>
                      Add document
                    </OutlineButton>
                  </Flex>

                  <VStack align="stretch" spacing={2}>
                    {form.customDocs.length === 0 ? (
                      <Text fontSize="12px" color="text.secondary" py={2}>
                        No custom documents yet.
                      </Text>
                    ) : (
                      form.customDocs.map((doc, index) => (
                        <Flex
                          key={doc.key || index}
                          gap={2}
                          align="center"
                          p={2}
                          bg="bg.input"
                          borderRadius="sm"
                          borderWidth="1px"
                          borderColor="border.subtle"
                        >
                          <Box flex="1" minW={0}>
                            <Input
                              h="36px"
                              fontSize="13px"
                              placeholder="e.g. Heavy testing certificate"
                              value={doc.label}
                              onChange={(e) =>
                                updateCustomDoc(index, { label: e.target.value })
                              }
                            />
                          </Box>
                          <RequirementToggle
                            value={doc.level || 'optional'}
                            onChange={(v) => updateCustomDoc(index, { level: v })}
                            options={CUSTOM_LEVEL_OPTIONS}
                          />
                          <IconButton
                            aria-label="Remove custom document"
                            icon={<MdDelete />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeCustomDoc(index)}
                          />
                        </Flex>
                      ))
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            gap={2}
            borderTopWidth="1px"
            borderColor="border.subtle"
            bg="bg.surface"
            zIndex={2}
          >
            <OutlineButton type="button" onClick={addModal.onClose} isDisabled={saving}>
              Cancel
            </OutlineButton>
            <PrimaryButton type="submit" isLoading={saving}>
              Save
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.onClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0} pb={2}>
            <Flex align="center" gap={3} wrap="wrap" pr={8}>
              <Text fontSize="18px" fontWeight="700">
                {selected?.name || 'Equipment type'}
              </Text>
              {selected ? <ActiveBadge active={selected.active} /> : null}
            </Flex>
            <Text fontSize="13px" color="text.secondary" fontWeight="500" mt={1}>
              {selected?.equipmentId || '—'}
              {selected?.category ? ` · ${selected.category}` : ''}
              {selected?.vehicleClass ? ` · ${selected.vehicleClass}` : ''}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
            {selected ? (
              <VStack align="stretch" spacing={5}>
                <Box>
                  <Text fontSize="12px" fontWeight="700" color="text.secondary" mb={2}>
                    BASICS
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <DetailRow label="ID" value={selected.equipmentId} mono />
                    <DetailRow label="Equipment type" value={selected.name} />
                    <DetailRow label="Category" value={selected.category} />
                    <DetailRow label="Vehicle class" value={selected.vehicleClass} />
                    <DetailRow
                      label="Created"
                      value={formatStamp(selected.createdAt)}
                    />
                    <DetailRow
                      label="Updated"
                      value={formatStamp(selected.updatedAt)}
                    />
                  </SimpleGrid>
                  <Box mt={3}>
                    <DetailRow
                      label="Description"
                      value={selected.description || 'No description'}
                    />
                  </Box>
                </Box>

                <Box>
                  <Text fontSize="12px" fontWeight="700" color="text.secondary" mb={2}>
                    FORM FIELDS (what sellers must fill)
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {EQUIPMENT_FIELD_KEYS.map(({ key, label }) => (
                      <RequirementChip
                        key={key}
                        label={label}
                        level={
                          selected.fieldRequirements?.[key] ||
                          defaultFieldRequirements()[key] ||
                          'optional'
                        }
                      />
                    ))}
                  </SimpleGrid>
                </Box>

                <Box>
                  <Text fontSize="12px" fontWeight="700" color="text.secondary" mb={2}>
                    DOCUMENTS (what sellers must upload)
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {EQUIPMENT_DOC_TYPES.map(({ key, label }) => (
                      <RequirementChip
                        key={key}
                        label={label}
                        level={
                          selected.docRequirements?.[key] ||
                          defaultDocRequirements()[key] ||
                          'optional'
                        }
                      />
                    ))}
                  </SimpleGrid>
                  {Array.isArray(selected.customDocs) && selected.customDocs.length > 0 ? (
                    <Box mt={3}>
                      <Text fontSize="12px" fontWeight="600" color="text.secondary" mb={2}>
                        Custom documents
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {selected.customDocs.map((doc) => (
                          <RequirementChip
                            key={doc.key || doc.label}
                            label={doc.label || doc.key || 'Custom document'}
                            level={doc.level === 'required' ? 'required' : 'optional'}
                          />
                        ))}
                      </SimpleGrid>
                    </Box>
                  ) : null}
                </Box>
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            gap={2}
            borderTopWidth="1px"
            borderColor="border.subtle"
            bg="bg.surface"
            zIndex={2}
          >
            <OutlineButton onClick={detailModal.onClose}>Close</OutlineButton>
            <PrimaryButton onClick={openEditFromDetails}>Edit</PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
